import Avatar from '@savvy-kit/components/avatar';
import { revealOffset, useRevealSequence } from '@savvy-kit/foundations/motion/reveal-sequence';
import { REVEAL } from '@savvy-kit/foundations/motion/tokens';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { DateTime } from 'luxon';
import { TRANSFER_TIMELINE_STEPS } from 'src/features/custodian-transfer/components/success-page/constants';
import { TransferTimelineCard } from 'src/features/custodian-transfer/components/success-page/transfer-timeline-card';
import { usePageTitle } from 'src/hooks/use-page-title';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useTrackPageView } from '@savvy/deprecated-shared/tracking/use-track-page-view';

import { CLIENT_PORTAL_PAGE_HEADING_CLASS, CLIENT_PORTAL_PAGE_PADDING_CLASS } from '../common/constants';
import { HomeAllocationCard } from './home-allocation-card';
import { HomeInvestmentsList } from './home-investments-list';
import { HomeTasksPanel } from './home-tasks-panel';
import { HomeValueCard } from './home-value-card';
import { useClientPortalHome } from './hooks/use-client-portal-home';

export function ClientPortalHome() {
  const { user } = useAuth();
  const home = useClientPortalHome();
  usePageTitle('Home');
  useTrackPageView();
  const firstName = user?.firstName ?? '';

  // One clock for the page: every surface holds its skeleton until every query has landed, then the whole
  // page cross-fades onto content and the choreography starts. Tasks is in the gate deliberately — a panel
  // arriving on its own is the thing that reads as the page still loading. Latched, so a single panel's
  // retry re-skeletons that panel alone rather than replaying the page.
  const phase = useRevealSequence(home.choreographyReady && !home.tasksLoading, { once: true });
  const skeleton = phase === 'loading';
  const beat = revealOffset(phase);

  return (
    <div className={cn(CLIENT_PORTAL_PAGE_PADDING_CLASS, 'pt-5 md:pt-8')}>
      <div className="mb-6 flex items-center gap-4">
        <span aria-hidden className="flex shrink-0">
          <Avatar showFallbackIcon size="2xl" imageUrl={user?.profilePicUrl} />
        </span>
        <div className="flex flex-col gap-1">
          <Text variant="h1" className={CLIENT_PORTAL_PAGE_HEADING_CLASS}>
            {firstName === '' ? 'Welcome back' : `Welcome back, ${firstName}`}
          </Text>
          <Text variant="body-sm">{DateTime.now().toFormat('cccc, LLLL d')}</Text>
        </div>
      </div>

      <div
        key={skeleton ? 'skeleton' : 'content'}
        className={cn('flex flex-col gap-5', !skeleton && 'animate-in fade-in fill-mode-both')}
        style={skeleton ? undefined : { animationDuration: `${REVEAL.duration * 1000}ms` }}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            <HomeValueCard
              valueLoading={skeleton || home.valueLoading}
              valueFailed={home.valueFailed}
              onRetryValue={home.retryValue}
              statsLoading={skeleton || home.statsLoading}
              statsFailed={home.statsFailed}
              onRetryStats={home.retryStats}
              timeRange={home.timeRange}
              timeRanges={home.timeRanges}
              onTimeRangeChange={home.setTimeRange}
              investmentsValue={home.investmentsValue}
              change={home.change}
              series={home.series}
              dayChange={home.dayChange}
              totalReturnAmount={home.totalReturnAmount}
              totalReturnPercent={home.totalReturnPercent}
              benchmarks={home.benchmarks}
              investmentsHref={home.investmentsHref}
              inceptionLabel={home.inceptionLabel}
              staleAsOfLabel={home.staleAsOfLabel}
              transferring={home.transferring}
              destinationCustodian={home.destinationCustodian}
              celebrationKey={home.celebrationKey}
              delayOffset={beat}
            />
          </div>

          <div className="w-full lg:w-80 lg:shrink-0">
            <HomeTasksPanel
              loading={skeleton || home.tasksLoading}
              failed={home.tasksFailed}
              onRetry={home.retryTasks}
              tasks={home.tasks}
              delayOffset={beat}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
          <HomeInvestmentsList
            loading={skeleton || home.valueLoading}
            failed={home.valueFailed}
            onRetry={home.retryValue}
            investments={home.investments}
            brandName={home.brandName}
            delayOffset={beat}
          />
          {home.transferring ? (
            <TransferTimelineCard
              steps={TRANSFER_TIMELINE_STEPS}
              activeStep={home.transferTimelineStep}
              advisor={home.transferAdvisor}
              showStepIcons
              titleVariant="h4"
              className="rounded-xl border border-border-light"
            />
          ) : (
            <HomeAllocationCard
              loading={skeleton || home.allocationLoading}
              failed={home.allocationFailed}
              onRetry={home.retryAllocation}
              slices={home.allocation}
              detailsHref={home.investmentsHref}
              delayOffset={beat}
            />
          )}
        </div>
      </div>
    </div>
  );
}
